import React, { SyntheticEvent, useEffect, useState } from "react";
import { ApiStatus, callApi } from "../../api/ApiUtilities";
import { User } from "../../data/User";
import LoginError from "./LoginError";

/** Matches the server's resend cooldown. */
export const RESEND_COOLDOWN_SECONDS = 60;

interface EmailCodeLoginProps {
  username: string;
  setUsername: (value: string) => void;
  onLoggedIn: (user: User) => void;
  onUsePassword: () => void;
}

/**
 * Sign in with a one-time code emailed to the user, instead of a password:
 * enter email -> "Email me a code" -> enter the 6-digit code -> signed in.
 */
function EmailCodeLogin({ username, setUsername, onLoggedIn, onUsePassword }: EmailCodeLoginProps) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  async function sendCode(event?: SyntheticEvent) {
    event?.preventDefault();
    if (!username.trim()) {
      setError("Enter your email address");
      return;
    }
    setBusy(true);
    setError("");
    const response = await callApi<string>("/api/auth/code", "POST", { username: username.trim() });
    setBusy(false);
    if (response.status === ApiStatus.Ok) {
      setMessage(response.message);
      setCode("");
      setStep("code");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } else {
      setError(response.message || "The code could not be sent. Please try again.");
    }
  }

  async function verifyCode(event: SyntheticEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const response = await callApi<User>("/api/auth/code/verify", "POST", { username: username.trim(), code });
    if (response.status === ApiStatus.Ok && response.data) {
      onLoggedIn(response.data);
    } else {
      setBusy(false);
      setError(response.message);
    }
  }

  if (step === "email") {
    return (
      <form onSubmit={sendCode}>
        <p className="text-muted small">We'll email you a 6-digit code to sign in with, instead of your password.</p>
        <div className="form-floating mb-3">
          <input
            type="email"
            className="form-control"
            id="codeEmail"
            placeholder="name@example.com"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <label htmlFor="codeEmail">Email address</label>
        </div>
        <button disabled={busy} className="w-100 btn btn-lg btn-primary" type="submit">
          {busy ? "Sending..." : "Email me a code"}
        </button>
        <LoginError error={error} />
        <p className="mt-3 mb-0 text-center">
          <button type="button" className="btn btn-link p-0" onClick={onUsePassword}>
            Sign in with your password instead
          </button>
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={verifyCode}>
      {message ? (
        <div className="alert alert-info py-2" role="status">
          {message}
        </div>
      ) : null}
      <div className="form-floating mb-3">
        <input
          type="text"
          className="form-control sl-code-input"
          id="loginCode"
          placeholder="123456"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9 ]/g, ""))}
          autoFocus
        />
        <label htmlFor="loginCode">6-digit code</label>
      </div>
      <button disabled={busy || code.replace(/\D/g, "").length !== 6} className="w-100 btn btn-lg btn-primary" type="submit">
        {busy ? "Signing in..." : "Sign in"}
      </button>
      <LoginError error={error} />
      <div className="mt-3 d-flex flex-wrap justify-content-between gap-2">
        <button type="button" className="btn btn-link p-0" disabled={busy || cooldown > 0} onClick={() => sendCode()}>
          {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
        </button>
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => {
            setStep("email");
            setError("");
            setMessage("");
          }}
        >
          Use a different email
        </button>
      </div>
      <p className="mt-2 mb-0 text-center">
        <button type="button" className="btn btn-link p-0" onClick={onUsePassword}>
          Sign in with your password instead
        </button>
      </p>
    </form>
  );
}

export default EmailCodeLogin;
